import { forwardRef, ReactNode } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Progress,
  Paper,
  ThemeIcon,
  RingProgress,
  SimpleGrid,
} from '@mantine/core';
import {
  IconTrendingUp,
  IconTrendingDown,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { Card, Title } from '../../ui';
import { FadeIn, StaggeredList } from '../../animations';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface BudgetCategory {
  id: string;
  name: string;
  icon?: ReactNode;
  color?: string;
  budgeted: number;
  spent: number;
  currency?: string;
}

export interface BudgetTrackerProps {
  title?: string;
  period?: string;
  categories: BudgetCategory[];
  totalBudget: number;
  totalSpent: number;
  currency?: string;
  variant?: 'cards' | 'list' | 'compact';
  showOverview?: boolean;
  onCategoryClick?: (category: BudgetCategory) => void;
  className?: string;
}

export const BudgetTracker = forwardRef<HTMLDivElement, BudgetTrackerProps>(
  (
    {
      title = 'Budget',
      period,
      categories,
      totalBudget,
      totalSpent,
      currency = 'USD',
      variant = 'list',
      showOverview = true,
      onCategoryClick,
      className,
    },
    ref
  ) => {
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const formatCurrency = (amount: number, cur = currency) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: cur,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    const getPercentage = (spent: number, budgeted: number) => {
      if (budgeted === 0) return 0;
      return Math.round((spent / budgeted) * 100);
    };

    const getProgressColor = (percentage: number) => {
      if (percentage >= 100) return 'red';
      if (percentage >= 80) return 'orange';
      if (percentage >= 60) return 'yellow';
      return 'green';
    };

    const totalPercentage = getPercentage(totalSpent, totalBudget);
    const remaining = totalBudget - totalSpent;

    const renderOverview = () => (
      <Paper p="lg" bg="gray.0" radius="md">
        <Group justify="space-between" align="center">
          <Box>
            <Text size="xs" c="dimmed" style={accessibilityStyles}>Total Budget</Text>
            <Text size="xl" fw={700} style={accessibilityStyles}>{formatCurrency(totalBudget)}</Text>
            {period && <Text size="xs" c="dimmed" style={accessibilityStyles}>{period}</Text>}
          </Box>
          <RingProgress
            size={100}
            thickness={10}
            roundCaps
            sections={[
              { value: Math.min(totalPercentage, 100), color: getProgressColor(totalPercentage) },
            ]}
            label={
              <Text ta="center" size="sm" fw={700} style={accessibilityStyles}>
                {totalPercentage}%
              </Text>
            }
          />
          <Stack gap="xs" align="flex-end">
            <Box ta="right">
              <Text size="xs" c="dimmed" style={accessibilityStyles}>Spent</Text>
              <Text size="lg" fw={600} c={totalPercentage >= 100 ? 'red' : undefined} style={accessibilityStyles}>
                {formatCurrency(totalSpent)}
              </Text>
            </Box>
            <Box ta="right">
              <Text size="xs" c="dimmed" style={accessibilityStyles}>Remaining</Text>
              <Text size="lg" fw={600} c={remaining < 0 ? 'red' : 'green'} style={accessibilityStyles}>
                {formatCurrency(remaining)}
              </Text>
            </Box>
          </Stack>
        </Group>
      </Paper>
    );

    const renderCategoryCard = (category: BudgetCategory) => {
      const percentage = getPercentage(category.spent, category.budgeted);
      const remaining = category.budgeted - category.spent;
      const isOverBudget = percentage >= 100;

      return (
        <Paper
          p="md"
          withBorder
          style={{ cursor: onCategoryClick ? 'pointer' : 'default' }}
          onClick={() => onCategoryClick?.(category)}
        >
          <Stack gap="sm">
            <Group justify="space-between">
              <Group gap="sm">
                {category.icon && (
                  <ThemeIcon size="sm" radius="xl" variant="light" color={category.color || 'blue'}>
                    {category.icon}
                  </ThemeIcon>
                )}
                <Text size="sm" fw={500} style={accessibilityStyles}>{category.name}</Text>
              </Group>
              {isOverBudget && (
                <ThemeIcon size="xs" color="red" variant="light" radius="xl">
                  <IconAlertTriangle size={12} />
                </ThemeIcon>
              )}
            </Group>

            <Box>
              <Group justify="space-between" mb={4}>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  {formatCurrency(category.spent, category.currency)} of {formatCurrency(category.budgeted, category.currency)}
                </Text>
                <Text size="xs" fw={500} c={getProgressColor(percentage)} style={accessibilityStyles}>
                  {percentage}%
                </Text>
              </Group>
              <Progress
                value={Math.min(percentage, 100)}
                color={getProgressColor(percentage)}
                size="sm"
              />
            </Box>

            <Group justify="space-between">
              <Text size="xs" c="dimmed" style={accessibilityStyles}>Remaining</Text>
              <Text size="xs" fw={500} c={remaining < 0 ? 'red' : 'green'} style={accessibilityStyles}>
                {formatCurrency(remaining, category.currency)}
              </Text>
            </Group>
          </Stack>
        </Paper>
      );
    };

    const renderCategoryListItem = (category: BudgetCategory) => {
      const percentage = getPercentage(category.spent, category.budgeted);
      const isOverBudget = percentage >= 100;

      return (
        <Paper
          p="sm"
          withBorder
          style={{ cursor: onCategoryClick ? 'pointer' : 'default' }}
          onClick={() => onCategoryClick?.(category)}
        >
          <Group justify="space-between" mb="xs">
            <Group gap="sm">
              {category.icon && (
                <ThemeIcon size="sm" radius="xl" variant="light" color={category.color || 'blue'}>
                  {category.icon}
                </ThemeIcon>
              )}
              <Box>
                <Text size="sm" fw={500} style={accessibilityStyles}>{category.name}</Text>
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  {formatCurrency(category.spent, category.currency)} / {formatCurrency(category.budgeted, category.currency)}
                </Text>
              </Box>
            </Group>
            <Group gap="xs">
              {isOverBudget && (
                <IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />
              )}
              <Text size="sm" fw={600} c={getProgressColor(percentage)} style={accessibilityStyles}>
                {percentage}%
              </Text>
            </Group>
          </Group>
          <Progress
            value={Math.min(percentage, 100)}
            color={getProgressColor(percentage)}
            size="xs"
          />
        </Paper>
      );
    };

    const renderCompactItem = (category: BudgetCategory) => {
      const percentage = getPercentage(category.spent, category.budgeted);

      return (
        <Group justify="space-between" py="xs">
          <Group gap="xs">
            {category.icon && (
              <ThemeIcon size="xs" radius="xl" variant="light" color={category.color || 'blue'}>
                {category.icon}
              </ThemeIcon>
            )}
            <Text size="sm" style={accessibilityStyles}>{category.name}</Text>
          </Group>
          <Group gap="sm">
            <Progress
              value={Math.min(percentage, 100)}
              color={getProgressColor(percentage)}
              size="xs"
              w={60}
            />
            <Text size="xs" fw={500} w={40} ta="right" style={accessibilityStyles}>
              {percentage}%
            </Text>
          </Group>
        </Group>
      );
    };

    return (
      <FadeIn>
        <Card ref={ref} className={className}>
          <Stack gap="md">
            {title && <Title order={4}>{title}</Title>}

            {showOverview && renderOverview()}

            <Box>
              {variant === 'cards' && (
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
                  <StaggeredList staggerDelay={50}>
                    {categories.map((category) => (
                      <Box key={category.id}>{renderCategoryCard(category)}</Box>
                    ))}
                  </StaggeredList>
                </SimpleGrid>
              )}

              {variant === 'list' && (
                <Stack gap="xs">
                  <StaggeredList staggerDelay={30}>
                    {categories.map((category) => (
                      <Box key={category.id}>{renderCategoryListItem(category)}</Box>
                    ))}
                  </StaggeredList>
                </Stack>
              )}

              {variant === 'compact' && (
                <Stack gap={0}>
                  {categories.map((category, index) => (
                    <Box key={category.id}>
                      {renderCompactItem(category)}
                      {index < categories.length - 1 && <Box h={1} bg="gray.2" />}
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

BudgetTracker.displayName = 'BudgetTracker';

export default BudgetTracker;
